import React, { useRef, useCallback, useState, useEffect } from "react";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCode } from "@fortawesome/pro-regular-svg-icons/faCode";

const IFrameContent = styled.div`
    position: relative;
    min-height: ${({ isEditMode }) => (isEditMode ? "60px" : "unset")};
`;

const IFrameOverlay = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
`;

let Icon = () => (
    <div className="flex py-10 justify-center items-center w-full">
        <FontAwesomeIcon icon={faCode} size="6x" />
    </div>
);

const EmbedComponent = ({ state: { url }, isEditMode }) => {
    const inStorefront = !window?.location?.pathname.includes("/content/edit/");

    return (
        <IFrameContent className="content__embed" isEditMode={isEditMode}>
            {url && url.length > 0 ? (
                <>
                    <SandboxedEmbed url={url} />
                    {isEditMode && <IFrameOverlay />}
                </>
            ) : inStorefront ? (
                <></>
            ) : (
                <Icon />
            )}
        </IFrameContent>
    );
};

// Renders embed HTML inside a sandboxed iframe so scripts execute and persist
// independently of React reconciliation. Memoised so the iframe is only
// replaced when the HTML content actually changes.
const SandboxedEmbed = React.memo(({ url }) => {
    const [height, setHeight] = useState(150);
    const iframeRef = useRef(null);

    const onMessage = useCallback((event) => {
        if (
            event.source === iframeRef.current?.contentWindow &&
            typeof event.data?.embedHeight === "number"
        ) {
            setHeight(event.data.embedHeight);
        }
    }, []);

    useEffect(() => {
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [onMessage]);

    const resizeScript = `<script>
        function reportHeight() {
            window.parent.postMessage({ embedHeight: document.body.scrollHeight }, "*");
        }
        window.addEventListener("load", reportHeight);
        new MutationObserver(reportHeight).observe(
            document.body,
            { childList: true, subtree: true, attributes: true }
        );
    </script>`;

    const srcdoc = `<!DOCTYPE html><html><head>
        <style>* { box-sizing: border-box; } body { margin: 0; padding: 0; }</style>
    </head><body>${url}${resizeScript}</body></html>`;

    return (
        <iframe
            ref={iframeRef}
            srcDoc={srcdoc}
            sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            style={{ width: "100%", height: `${height}px`, border: "none", display: "block" }}
            title="Embed content"
        />
    );
}, (prev, next) => prev.url === next.url);

export default EmbedComponent;
